import React, { useEffect, useState } from 'react';
import { StanInput, StanModal } from '@/components';
import type { MFAConfig } from '@/lib/api/settings/types';
import { useRenameMFA } from '../../../queries/useMFA';
import { stanNotify } from '@/lib/stanNotify';
import { handleApiError } from '@/lib/utils/apiErrorHandler';

interface RenameMFAModalProps {
    isOpen: boolean;
    onClose: () => void;
    config: MFAConfig | null;
    onSuccess: () => void;
}

export const RenameMFAModal: React.FC<RenameMFAModalProps> = ({ isOpen, onClose, config, onSuccess }) => {
    const [newName, setNewName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const { mutate: renameMFA, isPending } = useRenameMFA();

    // Reset form when modal opens with a new config
    useEffect(() => {
        if (isOpen && config) {
            setNewName(config.name || '');
            setError(null);
        }
    }, [isOpen, config]);

    const handleSave = () => {
        if (!config || !newName.trim()) return;

        setError(null);
        renameMFA(
            { configId: config.id, name: newName.trim() },
            {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Success!',
                        text: 'Successfully renamed 2FA method!',
                    });
                    onSuccess();
                    onClose();
                },
                onError: err => {
                    const result = handleApiError(err, 'Failed to rename 2FA method');
                    setError(
                        result.message ||
                            '2FA method name must contain only numbers, letters and punctuation characters'
                    );
                },
            }
        );
    };

    const handleClose = () => {
        setNewName('');
        setError(null);
        onClose();
    };

    return (
        <StanModal
            id='rename-mfa-modal'
            isOpen={isOpen}
            title='Rename 2FA method'
            onClose={handleClose}
            primaryActionLabel='Save'
            primaryActionDisabled={!newName.trim()}
            primaryActionLoading={isPending}
            onPrimaryAction={handleSave}
            secondaryActionLabel='Cancel'
            onSecondaryAction={handleClose}
            size='sm'
        >
            <div className='mt-2'>
                <StanInput
                    id='authenticator-name'
                    name='authenticator-name'
                    placeholder='Enter authenticator name'
                    value={newName}
                    onChange={e => {
                        setNewName(e.target.value);
                        setError(null);
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && newName.trim()) {
                            handleSave();
                        }
                    }}
                    error={!!error}
                    hintText={error || undefined}
                />
            </div>
        </StanModal>
    );
};
